/**
 * Changing a batch's quantity by hand.
 *
 * The one action behind everything that is not a sale or new stock: stock that
 * expired or broke and was thrown away, went back to the supplier, went missing,
 * or was simply counted wrong. The person types what is actually there now and
 * taps why. Whether three boxes went into a bin or back on a van makes no
 * difference to the count — ten becomes seven either way.
 *
 * The reason is not for the count. It is so an owner can tell stock that was
 * thrown away from stock that disappeared: "missing", from the same person,
 * week after week, is how theft becomes visible.
 *
 * Checked on the phone and on the server by this same function, so a refusal
 * is explained next to the field before anybody presses Save.
 */

export const ADJUST_REASONS = [
  "expired",
  "damaged",
  "missing",
  "returned",
  "counted_wrong",
  "other",
] as const;

export type AdjustReason = (typeof ADJUST_REASONS)[number];

/**
 * The only reasons that can put a number UP.
 *
 * Stock does not un-expire or come back from the supplier by itself. More on
 * the shelf than the app thinks means it was counted or typed wrong — or
 * something the owner will want to explain in words.
 */
export const RAISING_REASONS: readonly AdjustReason[] = ["counted_wrong", "other"];

/** Which reasons make sense for this change, in the order they are offered. */
export function reasonsFor(currentQty: number, newQty: number): readonly AdjustReason[] {
  return newQty > currentQty ? RAISING_REASONS : ADJUST_REASONS;
}

export type AdjustProblem =
  /** Not a whole number. Stock is counted in whole units, never parts. */
  | { code: "not_a_number" }
  /** A shelf cannot hold fewer than none. */
  | { code: "below_zero" }
  /** The same number again changes nothing and should not be recorded. */
  | { code: "unchanged" }
  /** Expired, damaged, missing or returned used to put the number UP. */
  | { code: "reason_cannot_raise" }
  /** "Other" with nothing written says nothing at all. */
  | { code: "note_required" };

export function checkAdjustment(input: {
  currentQty: number;
  newQty: number;
  reason: AdjustReason;
  note?: string | null;
}): AdjustProblem[] {
  const { currentQty, newQty, reason } = input;

  if (!Number.isInteger(newQty)) return [{ code: "not_a_number" }];
  if (newQty < 0) return [{ code: "below_zero" }];
  if (newQty === currentQty) return [{ code: "unchanged" }];

  const problems: AdjustProblem[] = [];
  if (newQty > currentQty && !RAISING_REASONS.includes(reason)) {
    problems.push({ code: "reason_cannot_raise" });
  }
  if (reason === "other" && !(input.note ?? "").trim()) {
    problems.push({ code: "note_required" });
  }
  return problems;
}
